import React, { useEffect, useMemo, useState } from "react";
import { ClockContainer, ClockLevel, Fill, TimerPill, TimerRow, Track } from "./Clock.styles";
import { useGameContext } from "../../contexts/GameContext";
import { useSoundContext } from "../../contexts/SoundContext";
import Icon from "../ui/Icon";

const Clock: React.FC = () => {
  const [blink, setBlink] = useState(false);
  const { voteTimeLimit, answerTimeLimit, showTime, votingStarted, settings } =
    useGameContext();
  const { playClockAudio, stopClockAudio, playBellAudio } = useSoundContext();

  const { answerTime, voteTime } = settings || {};

  const timeLimit = useMemo(
    () => ((votingStarted ? voteTime : answerTime) || 0) * 60000 + 1000,
    [votingStarted, voteTime, answerTime],
  );

  const clockTime = useMemo(
    () => (votingStarted ? voteTimeLimit : answerTimeLimit) + 1000,
    [answerTimeLimit, voteTimeLimit, votingStarted],
  );

  const shouldBlink = clockTime <= timeLimit * 0.2;

  useEffect(() => {
    if (shouldBlink) {
      const interval = setInterval(() => setBlink((b) => !b), 500);
      return () => clearInterval(interval);
    }
  }, [shouldBlink, stopClockAudio]);

  const shouldPlayAudio =
    clockTime <= timeLimit * 0.4 && clockTime > 0 && showTime;

  useEffect(() => {
    if (shouldPlayAudio) {
      playClockAudio();
    } else {
      stopClockAudio();
    }
  }, [playClockAudio, shouldPlayAudio, stopClockAudio]);

  const shouldPlayBell = showTime && clockTime <= 0;

  useEffect(() => {
    if (shouldPlayBell) {
      playBellAudio();
    }
  }, [playBellAudio, shouldPlayBell]);

  const level = useMemo<ClockLevel>(() => {
    if (clockTime > timeLimit * 0.6) {
      return "calm";
    }
    if (clockTime > timeLimit * 0.4) {
      return "warn";
    }
    return "bad";
  }, [clockTime, timeLimit]);

  const progress = timeLimit > 0 ? Math.max(0, Math.min(1, clockTime / timeLimit)) : 0;

  const show = showTime && !!Math.round((clockTime - 1000) / 1000);

  return (
    <ClockContainer $show={show}>
      {showTime && (
        <TimerPill
          $level={level}
          $tick={shouldBlink && blink}
          role="timer"
          aria-live="off"
        >
          <TimerRow>
            <Icon name="clock" size={18} />
            {new Date(clockTime > 0 ? clockTime : 1).toISOString().slice(14, 19)}
          </TimerRow>
          <Track>
            <Fill $level={level} style={{ width: `${progress * 100}%` }} />
          </Track>
        </TimerPill>
      )}
    </ClockContainer>
  );
};

export default Clock;
